// ============================================================
// Núcleo puro de la Edge Function pública que recibe las respuestas del
// cuestionario de riesgo psicosocial (sin sesión, código de acceso como
// única credencial). Deliberadamente sin dependencias del repo ni del
// navegador (solo Web Crypto, disponible tanto en el navegador como en
// Deno) para poder copiarse tal cual a supabase/functions/<nombre>/index.ts,
// siguiendo el patrón de learning-public/index.ts.
//
// CITA REAL (fuente): el contrato de resultado viene de la función de
// base de datos `psychosocial_submit_response` (migración
// 20260920000000): devuelve jsonb con `ok` y, si falla, `error` entre
// 'codigo_invalido', 'evaluacion_no_abierta' o 'respuestas_invalidas'.
//
// DECISIÓN PROPIA (implementación, no de la fuente — pendiente de
// validar con Kevin):
//   - La forma exacta del cuerpo esperado (`SubmitBody`), los códigos de
//     estado HTTP por cada caso, y que cualquier error de base de datos
//     no reconocido se oculta como 'error_interno' (sin reenviar el
//     mensaje original) son decisiones de este archivo, no del
//     contrato de la función SQL.
//   - No se registra (log) nada del cuerpo de la petición — ni el
//     código, ni las respuestas — en ningún mensaje de error.
//
// LIMITACIÓN DE TASA (desde el 22/09/2026, migraciones 20260922000000 y
// 20260926000000): cada petición pasa por las funciones SQL
// psychosocial_submit_response_limited / psychosocial_notice_limited,
// que aplican (a) un límite por IP de 60 peticiones/min, sumando GET del
// aviso y POST de respuestas (valor en psychosocial_ip_rate_limit()), y
// (b) bloqueo por código: 5 intentos fallidos en 10 min lo bloquean
// 30 min. Al excederse, esta función responde 429
// 'demasiadas_solicitudes'.
//
// RIESGO RESIDUAL: la IP se toma de x-forwarded-for, que se puede
// falsificar, y el bloqueo es por código, no por atacante — quien rote
// IPs puede seguir probando códigos distintos. Lo que hace inviable
// adivinar uno sigue siendo la entropía de 80 bits por código. Los
// errores de esta función distinguen solo tres casos
// genéricos. OJO: 'evaluacion_no_abierta' solo aparece cuando el código
// existe y no se ha usado, es decir, revela que ese código existe; se
// acepta porque con 80 bits de entropía adivinarlo no es viable. Los
// errores de base de datos no reconocidos se ocultan como
// 'error_interno'.
//
// PRIVACIDAD: esta función NUNCA debe registrar la dirección de red ni
// el navegador de quien responde (a diferencia de learning-public, que
// sí guarda esos dos datos de cada persona), ni escribir en ningún
// registro el código o las respuestas. Los registros propios de la
// plataforma de Supabase no están verificados y no dependen de este
// código.
//
// AVISO: la función también responde a GET ?id=<uuid> con los datos del
// aviso al trabajador (nombre y dirección de la empresa, título,
// estado, fecha límite, plazo de conservación, contacto y número de
// preguntas), tomados de la función SQL psychosocial_public_notice
// (migración 20260920000500). Solo se devuelven esos campos; nunca
// códigos, conteos ni respuestas.
// ============================================================

// COPIA de psicosocial-codigos.ts: cualquier cambio debe hacerse en
// ambos; la prueba de contrato lo vigila.
export const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const CODE_LENGTH = 16;

export function normalizeCode(input: string): string | null {
  if (typeof input !== 'string') return null;
  const stripped = input.replace(/[\s-]/g, '').toUpperCase();
  if (stripped.length !== CODE_LENGTH) return null;
  for (let i = 0; i < stripped.length; i++) {
    if (CODE_ALPHABET.indexOf(stripped[i]) === -1) return null;
  }
  return stripped;
}

export async function sha256Hex(text: string): Promise<string> {
  if (typeof crypto === 'undefined' || !crypto.subtle || typeof crypto.subtle.digest !== 'function') {
    throw new Error('crypto.subtle no está disponible en este entorno');
  }
  const bytes = new TextEncoder().encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', bytes);
  const hashBytes = new Uint8Array(hashBuffer);
  let hex = '';
  for (let i = 0; i < hashBytes.length; i++) {
    hex += hashBytes[i].toString(16).padStart(2, '0');
  }
  return hex;
}

export const ITEM_COUNT = 58;

export type SubmitBody = { code: string; answers: Record<string, number> };

export type ParsedSubmit =
  | { ok: true; codeHash: string; answers: Record<string, number> }
  | { ok: false; status: number; error: string };

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export async function parseSubmitRequest(raw: unknown): Promise<ParsedSubmit> {
  if (!isPlainObject(raw)) {
    return { ok: false, status: 400, error: 'solicitud_invalida' };
  }

  const body = raw as { code?: unknown; answers?: unknown };

  if (typeof body.code !== 'string') {
    return { ok: false, status: 400, error: 'codigo_invalido' };
  }

  const normalized = normalizeCode(body.code);
  if (normalized === null) {
    return { ok: false, status: 400, error: 'codigo_invalido' };
  }

  if (!isPlainObject(body.answers)) {
    return { ok: false, status: 400, error: 'solicitud_invalida' };
  }

  const answersRaw = body.answers as Record<string, unknown>;
  const keys = Object.keys(answersRaw);

  if (keys.length !== ITEM_COUNT) {
    return { ok: false, status: 400, error: 'respuestas_invalidas' };
  }

  const answers: Record<string, number> = {};
  for (let i = 1; i <= ITEM_COUNT; i++) {
    const key = String(i);
    if (!Object.prototype.hasOwnProperty.call(answersRaw, key)) {
      return { ok: false, status: 400, error: 'respuestas_invalidas' };
    }
    const value = answersRaw[key];
    if (
      typeof value !== 'number' ||
      !Number.isFinite(value) ||
      !Number.isInteger(value) ||
      value < 1 ||
      value > 4
    ) {
      return { ok: false, status: 400, error: 'respuestas_invalidas' };
    }
    answers[key] = value;
  }

  const codeHash = await sha256Hex(normalized);
  return { ok: true, codeHash, answers };
}

export type DbResult = { ok?: boolean; error?: string } | null;

const KNOWN_DB_ERRORS = ['codigo_invalido', 'evaluacion_no_abierta', 'respuestas_invalidas'];

export function mapDbResult(result: DbResult): { status: number; body: { ok: boolean; error?: string } } {
  if (result && result.ok === true) {
    return { status: 200, body: { ok: true } };
  }
  if (result && result.error === 'demasiadas_solicitudes') {
    return { status: 429, body: { ok: false, error: 'demasiadas_solicitudes' } };
  }
  if (result && typeof result.error === 'string' && KNOWN_DB_ERRORS.indexOf(result.error) !== -1) {
    return { status: 400, body: { ok: false, error: result.error } };
  }
  return { status: 500, body: { ok: false, error: 'error_interno' } };
}

export function corsHeaders(): Record<string, string> {
  return {
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Headers': 'authorization, x-client-info, apikey, content-type',
    'Access-Control-Allow-Methods': 'GET, POST, OPTIONS',
  };
}

export type RpcCall = (
  ip: string,
  codeHash: string,
  answers: Record<string, number>
) => Promise<{ data: unknown; error: unknown }>;

export const MAX_BODY_CHARS = 4096;

export type HandleResult = { status: number; body: { ok: boolean; error?: string } };

// Manejador completo, sin dependencias de Deno: recibe el método, el texto del
// cuerpo y la función que llama a la base. La Edge Function real solo conecta esto.
export async function handleSubmit(method: string, ip: string, bodyText: string, rpc: RpcCall): Promise<HandleResult> {
  if (method !== 'POST') {
    return { status: 405, body: { ok: false, error: 'metodo_no_soportado' } };
  }
  if (typeof bodyText !== 'string' || bodyText.length > MAX_BODY_CHARS) {
    return { status: 413, body: { ok: false, error: 'solicitud_demasiado_grande' } };
  }
  let raw: unknown;
  try {
    raw = JSON.parse(bodyText);
  } catch {
    return { status: 400, body: { ok: false, error: 'solicitud_invalida' } };
  }
  const parsed = await parseSubmitRequest(raw);
  if (!parsed.ok) {
    return { status: parsed.status, body: { ok: false, error: parsed.error } };
  }
  try {
    const result = await rpc(ip, parsed.codeHash, parsed.answers);
    if (result.error) {
      return mapDbResult(null);
    }
    return mapDbResult(result.data as DbResult);
  } catch {
    return mapDbResult(null);
  }
}

export function isUuid(text: unknown): boolean {
  return typeof text === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(text);
}

export type NoticeRpcCall = (ip: string, assessmentId: string) => Promise<{ data: unknown; error: unknown }>;

export type PublicNotice = {
  company_name: string;
  company_address: string | null;
  title: string;
  status: string;
  due_at: string | null;
  retention_days: number;
  contact_email: string | null;
  contact_phone: string | null;
  item_count: number;
};

function strOrNull(value: unknown): string | null | undefined {
  if (value === null || value === undefined) return null;
  return typeof value === 'string' ? value : undefined;
}

// Copia SOLO los campos permitidos, con su tipo verificado; cualquier otro dato se descarta.
export function buildNotice(data: unknown): PublicNotice | null {
  if (!isPlainObject(data)) return null;
  const d = data as Record<string, unknown>;
  const companyAddress = strOrNull(d.company_address);
  const dueAt = strOrNull(d.due_at);
  const contactEmail = strOrNull(d.contact_email);
  const contactPhone = strOrNull(d.contact_phone);
  if (
    typeof d.company_name !== 'string' ||
    typeof d.title !== 'string' ||
    typeof d.status !== 'string' ||
    typeof d.retention_days !== 'number' ||
    typeof d.item_count !== 'number' ||
    companyAddress === undefined ||
    dueAt === undefined ||
    contactEmail === undefined ||
    contactPhone === undefined
  ) {
    return null;
  }
  return {
    company_name: d.company_name,
    company_address: companyAddress,
    title: d.title,
    status: d.status,
    due_at: dueAt,
    retention_days: d.retention_days,
    contact_email: contactEmail,
    contact_phone: contactPhone,
    item_count: d.item_count,
  };
}

export type NoticeResult = { status: number; body: { ok: boolean; error?: string; notice?: PublicNotice } };

export async function handleNotice(method: string, ip: string, assessmentId: string | null, rpc: NoticeRpcCall): Promise<NoticeResult> {
  if (method !== 'GET') {
    return { status: 405, body: { ok: false, error: 'metodo_no_soportado' } };
  }
  if (!isUuid(assessmentId)) {
    return { status: 400, body: { ok: false, error: 'id_invalido' } };
  }
  try {
    const result = await rpc(ip, assessmentId as string);
    if (result.error) {
      return { status: 500, body: { ok: false, error: 'error_interno' } };
    }
    const envelope = result.data;
    if (!isPlainObject(envelope)) {
      return { status: 500, body: { ok: false, error: 'error_interno' } };
    }
    if (envelope.ok !== true) {
      if (envelope.error === 'demasiadas_solicitudes') {
        return { status: 429, body: { ok: false, error: 'demasiadas_solicitudes' } };
      }
      return { status: 500, body: { ok: false, error: 'error_interno' } };
    }
    if (envelope.notice === null || envelope.notice === undefined) {
      return { status: 404, body: { ok: false, error: 'evaluacion_no_encontrada' } };
    }
    const notice = buildNotice(envelope.notice);
    if (notice === null) {
      return { status: 500, body: { ok: false, error: 'error_interno' } };
    }
    return { status: 200, body: { ok: true, notice } };
  } catch {
    return { status: 500, body: { ok: false, error: 'error_interno' } };
  }
}
